import { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ChevronDown, Package, ChefHat, UtensilsCrossed, Upload, Settings, ClipboardList, Users, Factory, ClipboardCheck, LogOut, Loader2 } from 'lucide-react';

// Pages
import IngredientsPage from "./pages/IngredientsPage";
import SubRecipesPage from "./pages/SubRecipesPage";
import FinalRecipesPage from "./pages/FinalRecipesPage";
import ImportPage from "./pages/ImportPage";
import SettingsPage from "./pages/SettingsPage";
import OrdersPage from "./pages/OrdersPage";
import ProductionPage from "./pages/ProductionPage";
import StockCheckPage from "./pages/StockCheckPage";
import LoginPage from "./pages/LoginPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send auth token
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auth Context
export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('auth_token');
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      sessionStorage.removeItem('auth_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    sessionStorage.setItem('auth_token', userData.token);
    setUser(userData);
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {}
    sessionStorage.removeItem('auth_token');
    setUser(false);
  };

  const isAdmin = user && user.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  
  const isRecipesActive = ['/ingredients', '/sub-recipes', '/recipes', '/import', '/stock-check'].some(
    path => location.pathname.startsWith(path)
  );

  const isOrdersActive = location.pathname.startsWith('/orders');
  
  const subNavLinkClass = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center gap-2 px-4 py-2.5 text-sm transition-colors duration-200 rounded-lg mx-1 ${
      isActive 
        ? 'bg-[var(--color-primary-light)] text-[var(--color-primary-dark)] font-medium' 
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`;
  };
  
  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200" data-testid="main-navigation">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2">
            <img 
              src="https://order.munchdmeals.com/munchd-logo.png" 
              alt="Munchd Meals" 
              className="h-9"
              style={{ borderRadius: '6px' }}
            />
          </NavLink>
          
          {/* Nav Links */}
          <div className="flex items-center gap-1">
            {/* Recipes Dropdown - Admin only */}
            {isAdmin && (
              <div className="relative group">
                <button
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                    isRecipesActive ? 'text-[var(--color-primary-dark)] bg-[var(--color-primary-50)]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                  data-testid="nav-recipes-menu"
                >
                  <ChefHat className="w-4 h-4" />
                  Recipes
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                
                <div className="absolute left-0 mt-1 w-52 bg-white border border-gray-200 shadow-lg rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-1 overflow-hidden">
                  <NavLink to="/ingredients" className={subNavLinkClass('/ingredients')} data-testid="nav-ingredients">
                    <Package className="w-4 h-4" />
                    Ingredients
                  </NavLink>
                  <NavLink to="/sub-recipes" className={subNavLinkClass('/sub-recipes')} data-testid="nav-sub-recipes">
                    <ChefHat className="w-4 h-4" />
                    Sub-Recipes
                  </NavLink>
                  <NavLink to="/recipes" className={subNavLinkClass('/recipes')} data-testid="nav-recipes">
                    <UtensilsCrossed className="w-4 h-4" />
                    Final Recipes
                  </NavLink>
                  <div className="border-t border-gray-100 my-1"></div>
                  <NavLink to="/import" className={subNavLinkClass('/import')} data-testid="nav-import">
                    <Upload className="w-4 h-4" />
                    Import Data
                  </NavLink>
                  <NavLink to="/stock-check" className={subNavLinkClass('/stock-check')} data-testid="nav-stock-check">
                    <ClipboardCheck className="w-4 h-4" />
                    Stock Check
                  </NavLink>
                </div>
              </div>
            )}

            {/* Orders - Admin only */}
            {isAdmin && (
              <NavLink
              to="/orders"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                isOrdersActive ? 'text-[var(--color-primary-dark)] bg-[var(--color-primary-50)]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
              data-testid="nav-orders"
            >
              <ClipboardList className="w-4 h-4" />
              Orders
            </NavLink>
            )}

            {/* Production */}
            <NavLink
              to="/production"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                location.pathname.startsWith('/production') ? 'text-[var(--color-primary-dark)] bg-[var(--color-primary-50)]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
              data-testid="nav-production"
            >
              <Factory className="w-4 h-4" />
              Production
            </NavLink>
            
            {/* Settings (Admin only) */}
            {isAdmin && (
              <NavLink
                to="/settings"
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                  location.pathname === '/settings' ? 'text-[var(--color-primary-dark)] bg-[var(--color-primary-50)]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                }`}
                data-testid="nav-settings"
              >
                <Settings className="w-4 h-4" />
                Settings
              </NavLink>
            )}
          </div>
          
          {/* User Info & Logout */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{user?.name || user?.user_id}</span>
            <span className={`badge-pill ${isAdmin ? 'badge-teal' : 'badge-gray'}`}>
              {user?.role === 'admin' ? 'Admin' : 'Operations'}
            </span>
            <button
              onClick={logout}
              className="btn-outline px-3 py-1.5 text-xs flex items-center gap-1"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Dashboard Stats Component
const DashboardStats = () => {
  const [stats, setStats] = useState({ ingredients_count: 0, sub_recipes_count: 0, recipes_count: 0, orders_count: 0, clients_count: 0 });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);
  
  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        <div className="progress-bar">
          <div className="progress-bar-fill"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="dashboard-stats">
      <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
        Dashboard
      </h1>
      <p className="text-gray-500 mb-8">Production management overview</p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <NavLink to="/ingredients" className="card p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Ingredients
          </div>
          <div className="text-3xl font-semibold tabular-nums text-gray-900" style={{ fontFamily: 'var(--font-mono)' }}>
            {stats.ingredients_count}
          </div>
        </NavLink>
        <NavLink to="/sub-recipes" className="card p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Sub-Recipes
          </div>
          <div className="text-3xl font-semibold tabular-nums text-gray-900" style={{ fontFamily: 'var(--font-mono)' }}>
            {stats.sub_recipes_count}
          </div>
        </NavLink>
        <NavLink to="/recipes" className="card p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Final Recipes
          </div>
          <div className="text-3xl font-semibold tabular-nums text-gray-900" style={{ fontFamily: 'var(--font-mono)' }}>
            {stats.recipes_count}
          </div>
        </NavLink>
        <NavLink to="/orders" className="card p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Weekly Orders
          </div>
          <div className="text-3xl font-semibold tabular-nums text-gray-900" style={{ fontFamily: 'var(--font-mono)' }}>
            {stats.orders_count || 0}
          </div>
        </NavLink>
        <NavLink to="/orders" className="card p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Clients
          </div>
          <div className="text-3xl font-semibold tabular-nums text-gray-900" style={{ fontFamily: 'var(--font-mono)' }}>
            {stats.clients_count || 0}
          </div>
        </NavLink>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NavLink to="/orders" className="card p-6 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-[var(--color-primary)]" />
            </div>
            <span className="font-semibold text-gray-900">Weekly Orders</span>
          </div>
          <p className="text-sm text-gray-500">
            Import weekly order sheets, manage production days, and add last-minute orders
          </p>
        </NavLink>
        <NavLink to="/import" className="card p-6 hover:shadow-md transition-shadow" style={{ borderRadius: '12px' }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
              <Upload className="w-4 h-4 text-[var(--color-primary)]" />
            </div>
            <span className="font-semibold text-gray-900">Import Recipes</span>
          </div>
          <p className="text-sm text-gray-500">
            Import ingredients, sub-recipes, and final recipes from CSV/Excel files
          </p>
        </NavLink>
      </div>
    </div>
  );
};

function AppContent() {
  const { user, isAdmin, loading, login } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  // Operations users only see Production
  if (!isAdmin) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Navigation />
        <Routes>
          <Route path="/production" element={<ProductionPage />} />
          <Route path="*" element={<Navigate to="/production" replace />} />
        </Routes>
        <Toaster position="bottom-right" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <Navigation />
      <Routes>
        <Route path="/" element={<DashboardStats />} />
        <Route path="/ingredients" element={<IngredientsPage />} />
        <Route path="/sub-recipes" element={<SubRecipesPage />} />
        <Route path="/recipes" element={<FinalRecipesPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/production" element={<ProductionPage />} />
        <Route path="/stock-check" element={<StockCheckPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
